/**
 * BankGuide: the visual guide of the question bank (/questions), opened by the "?" after the page's title
 * (HelpTitle helpId="bank"). The reference guide for every other module:
 * - HelpKit blocks in their order: intro, how it fits together, the steps with Mini pictures of the real controls,
 *   who can do what, good to know; the texts live under help.bank.* and name the buttons through the buttons' own
 *   i18n keys, so the guide says what the screen says.
 * - It follows the screen it explains: phones get the phone layout's controls (FolderBar's location button and "⋯"
 *   under 768px, the question cards under 768px, the "Filters" toggle and the selection bar's × under 640px: the
 *   same breakpoints as the page; four view tabs in two rows of two on phones), and the reader's role decides what
 *   is shown (the role in step 2, the Tasks and variant tabs, a review task's filters, the move step, the admin's tip).
 * Props, from the page: `canMove` (the page's rule for selecting and moving questions; without it there is no
 * move step), `emptyFolders` (how many folders wait in FolderBar's "N more" menu; its tip shows only then),
 * `inSubject` (a subject is open: its filter row has no subject select).
 */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  AdjustmentsHorizontalIcon,
  ArrowUpTrayIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  ChevronUpDownIcon,
  ClipboardDocumentCheckIcon,
  DocumentDuplicateIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  FolderArrowDownIcon,
  FolderIcon,
  FolderOpenIcon,
  InboxIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  QueueListIcon,
  ShareIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  TableCellsIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HelpFlow, HelpIntro, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  Mark,
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniChip,
  MiniDivider,
  MiniField,
  MiniIcon,
  MiniLevel,
  MiniMenu,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStatus,
  MiniTable,
  MiniTabs,
  MiniText,
} from "../Mini";
import { statusText } from "../../QuestionList/questionStatus";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canBuildVariants, canCreateIn, canReviewIn, canSeeTasksIn, canShareFolder, isAdminUser } from "../../../utils/folders";

const ROLE_ICONS = { admin: KeyIcon, moderator: ShieldCheckIcon, expert: CheckBadgeIcon, author: PencilIcon, viewer: EyeIcon };

export default function BankGuide({ canMove = true, emptyFolders = 0, inSubject = true }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  // The page's own breakpoints: FolderBar and the question list (table / cards) at md, FilterPanel at sm.
  const wide = useMediaQuery("(min-width: 768px)");
  const roomy = useMediaQuery("(min-width: 640px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const status = (code) => statusText(t, code);

  const isAdmin = isAdminUser(user);
  const folderRoles = user?.folder_roles || [];
  // Step 2 draws the reader's own role when they have one (admins: admin), else an author's.
  const role = isAdmin ? "admin" : folderRoles.length === 1 ? folderRoles[0] : "author";
  const RoleIcon = ROLE_ICONS[role] || EyeIcon;
  const subjectIcon = isAdmin ? undefined : RoleIcon;
  const roleTitle = t("folders.role_title", { role: t(`folders.roles.${role}`, { defaultValue: role }) });
  const writes = canCreateIn(role);
  const shares = canShareFolder(role);
  // The Subject variant tab is there for a subject's moderator and admins; the Tasks tab for them, its authors and its
  // experts (FolderBar's ViewTabs). Four tabs stand in two rows of two on phones.
  const variantTab = isAdmin || folderRoles.some(canBuildVariants);
  const tasksTab = isAdmin || folderRoles.some(canSeeTasksIn);
  const viewTabs = [
    { label: t("folders.tab_questions"), icon: QueueListIcon },
    { label: t("folders.tab_matrix"), icon: TableCellsIcon },
    tasksTab && { label: t("folders.tab_tasks"), icon: ClipboardDocumentCheckIcon },
    variantTab && { label: t("folders.tab_variant"), icon: DocumentDuplicateIcon },
  ].filter(Boolean);
  const tabsGrid = !wide && viewTabs.length === 4;
  // A review task opens the list on its questions (?task=): those who review or give review tasks meet its filters.
  const reviews = isAdmin || folderRoles.some(canReviewIn);

  const create = t("folders.create_question");
  const upload = t("folders.upload_from_file");
  const share = t("folders.share.button");
  const subjectPath = `${ex("exam")} · ${ex("subject")}`;

  // Sample questions: short status labels, so nothing is cut in any language.
  const questions = [
    { key: "q1", name: ex("question_1"), status: "APPROVED", level: "A" },
    { key: "q2", name: ex("question_2"), status: "UNDER_REVIEW", level: "B" },
    { key: "q3", name: ex("question_3"), status: "DECLINED", level: "C" },
  ];
  // A question on phones (QuestionCards): its name, status and difficulty, with a checkbox where it can be selected.
  const questionCard = (q, { check, mark = false } = {}) => {
    const name = (
      <MiniText variant="strong" truncate>
        {q.name}
      </MiniText>
    );
    return (
      <MiniCard key={q.key} icon={check === undefined ? undefined : <MiniCheck checked={check} />}>
        {mark ? (
          <Mark pointer round="md">
            {name}
          </Mark>
        ) : (
          name
        )}
        <MiniRow>
          <MiniStatus code={q.status} />
          <MiniLevel level={q.level} />
        </MiniRow>
      </MiniCard>
    );
  };

  return (
    <>
      <HelpIntro>
        <HelpText i18nKey="help.bank.intro" values={{ exam: ex("exam") }} />
      </HelpIntro>

      <HelpFlow pipeline="bts" current="questions" />

      <HelpSteps>
        <HelpStep
          title={t("help.bank.steps.folder.title")}
          illustration={
            wide ? (
              <MiniScreen title={t("nav.question_bank")}>
                <MiniRow>
                  <MiniText variant="caps">{t("folders.label")}</MiniText>
                  <MiniChip variant="folder">{t("folders.all_questions")}</MiniChip>
                  <MiniChip variant="folder" selected count={139} mark="1">
                    {ex("exam")}
                  </MiniChip>
                  <MiniChip variant="folder" count={64}>
                    {ex("exam_2")}
                  </MiniChip>
                  {emptyFolders > 0 && (
                    <MiniButton variant="quiet" chevron>
                      {t("folders.more_folders", { count: emptyFolders })}
                    </MiniButton>
                  )}
                </MiniRow>
                <MiniDivider />
                <MiniRow>
                  <MiniText variant="muted">{t("folders.subjects_label")}:</MiniText>
                  <MiniChip variant="subject" count={139}>
                    {t("folders.all_subjects")}
                  </MiniChip>
                  {/* Subjects carry the reader's role icon, but for admins (FolderBar's SubjectChip). */}
                  <MiniChip variant="subject" selected count={52} icon={subjectIcon} mark="2">
                    {ex("subject")}
                  </MiniChip>
                  <MiniChip variant="subject" count={87} icon={subjectIcon}>
                    {ex("subject_2")}
                  </MiniChip>
                </MiniRow>
              </MiniScreen>
            ) : (
              <MiniScreen title={t("nav.question_bank")}>
                <MiniCard tone="blue" icon={FolderOpenIcon} aside={<MiniIcon icon={ChevronUpDownIcon} />} mark="1">
                  <MiniStack tight>
                    <MiniText variant="muted" truncate>
                      {t("folders.label")}
                    </MiniText>
                    <MiniText variant="strong" truncate>
                      {t("folders.all_questions")}
                    </MiniText>
                  </MiniStack>
                </MiniCard>
                <MiniMenu
                  full
                  title={t("folders.choose_location")}
                  items={[
                    { label: t("folders.all_questions"), icon: Squares2X2Icon, selected: true },
                    { label: t("folders.no_folder"), icon: InboxIcon },
                    { label: ex("exam_2"), icon: FolderIcon, hint: 64 },
                    { label: ex("exam"), icon: FolderOpenIcon, hint: 139 },
                    { label: ex("subject"), indent: true, hint: 52, mark: "2" },
                    { label: ex("subject_2"), indent: true, hint: 87 },
                  ]}
                />
              </MiniScreen>
            )
          }
        >
          {wide ? t("help.bank.steps.folder.text") : t("help.bank.steps.folder.text_phone")}
          {/* The Folders page (/folders) shows the same folders as a Finder window. */}
          <p>
            <HelpText i18nKey="help.bank.steps.folder.text_page" values={{ page: t("nav.folders") }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.bank.steps.role.title")}
          illustration={
            <MiniScreen title={subjectPath}>
              {wide ? (
                <MiniRow className="justify-between gap-y-2.5">
                  <MiniRow>
                    <MiniText variant="title">{ex("subject")}</MiniText>
                    <MiniBadge icon={RoleIcon} mark={{ pointer: false }}>
                      {roleTitle}
                    </MiniBadge>
                  </MiniRow>
                  <MiniRow>
                    {shares && (
                      <MiniButton variant="quiet" icon={ShareIcon}>
                        {share}
                      </MiniButton>
                    )}
                    {writes && (
                      <MiniButton variant="quiet" icon={ArrowUpTrayIcon}>
                        {upload}
                      </MiniButton>
                    )}
                    {writes && (
                      <MiniButton variant="primary" icon={PlusIcon}>
                        {create}
                      </MiniButton>
                    )}
                    {isAdmin && <MiniButton variant="quiet" icon={EllipsisHorizontalIcon} />}
                  </MiniRow>
                </MiniRow>
              ) : (
                <>
                  <MiniRow>
                    <MiniBadge icon={RoleIcon} mark={{ pointer: false }}>
                      {roleTitle}
                    </MiniBadge>
                  </MiniRow>
                  {writes && (
                    <MiniRow className="flex-nowrap">
                      <MiniButton variant="primary" icon={PlusIcon}>
                        {create}
                      </MiniButton>
                      <MiniButton variant="outline" icon={EllipsisHorizontalIcon} />
                    </MiniRow>
                  )}
                </>
              )}
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.bank.steps.role.text" values={{ role: roleTitle, create, upload }} />
          {/* Phones keep one main button and put the others in "⋯" (FolderBar). */}
          {!wide && writes && (
            <p>
              <HelpText i18nKey={shares ? "help.bank.steps.role.text_phone" : "help.bank.steps.role.text_phone_upload"} values={{ upload, share }} />
            </p>
          )}
        </HelpStep>

        <HelpStep
          title={t("help.bank.steps.views.title")}
          illustration={
            <MiniScreen title={subjectPath}>
              {tabsGrid ? (
                // Two rows of two, with room for the cursor under the marked tab.
                <MiniStack>
                  <MiniTabs fill items={viewTabs.slice(0, 2)} current={1} mark={1} />
                  <MiniTabs fill items={viewTabs.slice(2)} current={-1} />
                </MiniStack>
              ) : (
                <MiniTabs fill items={viewTabs} current={1} mark={1} />
              )}
              <MiniTable
                columns={[
                  t("folders.matrix.col_topic_sub"),
                  ...["A", "B", "C"].map((level) => ({ label: <MiniLevel level={level} points={false} />, width: "2.75rem", align: "center" })),
                ]}
                rows={[
                  [ex("topic"), "2", "1", "1"],
                  [ex("topic_2"), "1", "2", "0"],
                ]}
              />
            </MiniScreen>
          }
        >
          <HelpText
            i18nKey="help.bank.steps.views.text"
            values={{ list: t("folders.tab_questions"), matrix: t("folders.tab_matrix") }}
          />
          {tasksTab && (
            <p>
              <HelpText i18nKey="help.bank.steps.views.text_tasks" values={{ tasks: t("folders.tab_tasks") }} />
            </p>
          )}
          {variantTab && (
            <p>
              <HelpText i18nKey="help.bank.steps.views.text_variant" values={{ variant: t("folders.tab_variant") }} />
            </p>
          )}
        </HelpStep>

        <HelpStep
          title={t("help.bank.steps.filters.title")}
          illustration={
            <MiniStack>
              <MiniRow scroll={!roomy}>
                <MiniChip selected count={12}>
                  {t("question_list.status_all")}
                </MiniChip>
                <MiniChip dot="amber" count={5} mark={roomy}>
                  {status("PENDING_REVIEW")}
                </MiniChip>
                <MiniChip dot="green" count={7}>
                  {status("APPROVED")}
                </MiniChip>
                <MiniChip dot="red" count={0}>
                  {status("DECLINED")}
                </MiniChip>
              </MiniRow>
              <MiniCard>
                {roomy ? (
                  <MiniRow>
                    <MiniField icon={MagnifyingGlassIcon} placeholder={t("question_list.search_placeholder")} className="flex-1" />
                    <MiniField select placeholder={t("questions_page.filters.all_topics")} />
                    <MiniField select placeholder={t("difficulty.filter_all")} />
                    {/* A quiet toggle from sm up, a framed one next to the search on phones (FilterPanel). */}
                    <MiniButton variant="quiet" icon={AdjustmentsHorizontalIcon} chevron>
                      {t("question_list.more_filters")}
                    </MiniButton>
                  </MiniRow>
                ) : (
                  <MiniRow className="flex-nowrap">
                    <MiniField icon={MagnifyingGlassIcon} placeholder={t("question_list.search_placeholder")} className="flex-1" />
                    <MiniButton variant="outline" icon={AdjustmentsHorizontalIcon} chevron mark>
                      {t("question_list.filters_short")}
                    </MiniButton>
                  </MiniRow>
                )}
              </MiniCard>
            </MiniStack>
          }
        >
          {/* Outside a subject the row starts with the subject select. */}
          {roomy ? (
            <HelpText
              i18nKey={inSubject ? "help.bank.steps.filters.text" : "help.bank.steps.filters.text_all"}
              values={{ more: t("question_list.more_filters") }}
            />
          ) : (
            <HelpText i18nKey="help.bank.steps.filters.text_phone" values={{ filters: t("question_list.filters_short") }} />
          )}
          {/* A review task's «Open the questions» filters the list by the task (?task=, &task_left=true). */}
          {reviews && (
            <p>
              <HelpText
                i18nKey="help.bank.steps.filters.text_task"
                values={{
                  task: t("folders.tasks.task_chip"),
                  left: `${t("folders.tasks.left_chip")}: ${t("folders.tasks.left_chip_value")}`,
                }}
              />
            </p>
          )}
        </HelpStep>

        <HelpStep
          title={t("help.bank.steps.open.title")}
          illustration={
            wide ? (
              <MiniTable
                columns={[
                  { label: t("question_list.columns.question"), width: "40%" },
                  t("question_list.columns.status"),
                  { label: t("difficulty.label"), width: "5.5rem" },
                ]}
                rows={questions.map((q) => [
                  <MiniText variant="strong" truncate>
                    {q.name}
                  </MiniText>,
                  <MiniStatus code={q.status} />,
                  <MiniLevel level={q.level} />,
                ])}
                markRow={0}
              />
            ) : (
              <MiniStack>{questions.slice(0, 2).map((q, index) => questionCard(q, { mark: index === 0 }))}</MiniStack>
            )
          }
        >
          <HelpText i18nKey="help.bank.steps.open.text" values={{ edit: t("buttons.edit") }} />
        </HelpStep>

        {canMove && (
          <HelpStep
            title={t("help.bank.steps.move.title")}
            illustration={
              <MiniStack>
                {/* SelectionBar in the count line's place: the count and a quiet "Clear" (an × before the count under
                    sm, its breakpoint), then the one main button. */}
                <MiniCard tone="blue">
                  {roomy ? (
                    <MiniRow className="justify-between gap-y-3">
                      <MiniRow>
                        <MiniText variant="strong">{t("folders.move.selected", { count: 2 })}</MiniText>
                        <MiniButton variant="quiet" icon={XMarkIcon}>
                          {t("folders.move.clear")}
                        </MiniButton>
                      </MiniRow>
                      <MiniButton variant="primary" icon={FolderArrowDownIcon} mark>
                        {t("folders.move.button")}
                      </MiniButton>
                    </MiniRow>
                  ) : (
                    <MiniRow className="justify-between flex-nowrap">
                      <MiniRow className="flex-nowrap">
                        <MiniButton variant="quiet" icon={XMarkIcon} />
                        <MiniText variant="strong" truncate>
                          {t("folders.move.selected", { count: 2 })}
                        </MiniText>
                      </MiniRow>
                      <MiniButton variant="primary" icon={FolderArrowDownIcon} mark>
                        {t("folders.move.button")}
                      </MiniButton>
                    </MiniRow>
                  )}
                </MiniCard>
                {wide ? (
                  <MiniTable
                    columns={[{ label: "", width: "2rem" }, t("question_list.columns.question"), { label: t("question_list.columns.status"), width: "38%" }]}
                    rows={questions.map((q, index) => [
                      <MiniCheck checked={index < 2} />,
                      <MiniText variant="strong" truncate>
                        {q.name}
                      </MiniText>,
                      <MiniStatus code={q.status} />,
                    ])}
                  />
                ) : (
                  questions.slice(0, 2).map((q) => questionCard(q, { check: true }))
                )}
              </MiniStack>
            }
          >
            {/* Under sm the bar shows "Clear" as an × alone. */}
            <HelpText i18nKey="help.bank.steps.move.text" values={{ move: t("folders.move.button"), clear: roomy ? t("folders.move.clear") : "×" }} />
            <p>{t("help.bank.steps.move.booklet")}</p>
          </HelpStep>
        )}

        <HelpStep
          title={t("help.bank.steps.texts.title")}
          illustration={
            <MiniScreen title={t("nav.question_bank")}>
              <MiniTabs
                items={[
                  { label: t("passages.tab_questions"), icon: QueueListIcon },
                  { label: t("passages.tab_texts"), icon: BookOpenIcon },
                ]}
                current={0}
                mark={1}
              />
              <MiniCard>
                <MiniRow>
                  <MiniText variant="strong">{ex("question_2")}</MiniText>
                  <MiniBadge icon={BookOpenIcon} tone="indigo">
                    {ex("passage")}
                  </MiniBadge>
                </MiniRow>
              </MiniCard>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.bank.steps.texts.text" values={{ texts: t("passages.tab_texts") }} />
        </HelpStep>
      </HelpSteps>

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.bank.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.bank.roles.moderator") },
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.bank.roles.expert") },
          { key: "author", role: t("folders.roles.author"), Icon: PencilIcon, can: t("help.bank.roles.author") },
          { key: "viewer", role: t("folders.roles.viewer"), Icon: EyeIcon, can: t("help.bank.roles.viewer") },
        ]}
      />

      <HelpTips
        items={[
          // Phones have this in step 1; "N more" is the wide folder row's (phones list every folder in the sheet).
          wide && t("help.bank.tips.phone"),
          wide && emptyFolders > 0 && (
            <HelpText key="more" i18nKey="help.bank.tips.more" values={{ more: t("folders.more_folders", { count: emptyFolders }) }} />
          ),
          <HelpText key="drafts" i18nKey="help.bank.tips.drafts" values={{ mine: t("nav.my_questions") }} />,
          // Only admins have the folder and subject settings and deletes in "⋯".
          isAdmin && <HelpText key="menu" i18nKey="help.bank.tips.menu" />,
        ]}
      />
    </>
  );
}
